import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export type AgencyRateType = 'fannie_mf' | 'freddie_mf';

/**
 * Full spread row — mirrors `AgencySpreadRow` in `@arcanum-hq/supabase`. Kept
 * local so apps/web continues to have zero direct dep on server packages; the
 * cross-domain contract is the `GET /api/market-data/rate-spreads` JSON shape.
 */
export interface AgencySpreadRow {
  rate_type: AgencyRateType;
  display_name: string;
  spread_bps: number;
  source: 'auto' | 'manual';
  confidence_note: string | null;
  last_auto_update: string | null;
  last_manual_update: string | null;
  updated_by: string | null;
  updated_at: string;
}

interface ApiSpreadsListResponse {
  success: boolean;
  data: AgencySpreadRow[];
}

interface ApiSpreadsRowResponse {
  success: boolean;
  data: AgencySpreadRow;
}

/** Admin list of both agency spread rows. */
export function useAgencySpreads() {
  return useQuery<AgencySpreadRow[]>({
    queryKey: queryKeys.rateSpreads.list(),
    queryFn: async () => {
      const res = await api.get<ApiSpreadsListResponse>('/api/market-data/rate-spreads');
      return res.data;
    },
    staleTime: 60_000,
  });
}

export interface UpdateSpreadVars {
  rateType: AgencyRateType;
  spread_bps: number;
  note?: string;
}

/**
 * Admin override mutation. Invalidates both the spreads list AND the Cockpit
 * panel query — the panel reads spreads live so an override should surface
 * within seconds on any mounted Cockpit tab.
 */
export function useUpdateAgencySpread() {
  const qc = useQueryClient();
  return useMutation<AgencySpreadRow, Error, UpdateSpreadVars>({
    mutationFn: async ({ rateType, spread_bps, note }) => {
      const body: { spread_bps: number; note?: string } = { spread_bps };
      if (note) body.note = note;
      const res = await api.put<ApiSpreadsRowResponse>(
        `/api/market-data/rate-spreads/${rateType}`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.rateSpreads.list() });
      qc.invalidateQueries({ queryKey: queryKeys.marketRates.panel() });
    },
  });
}

/**
 * Revert-to-auto mutation. Same dual-invalidation as update — the Cockpit
 * panel reflects the new auto value on the next fetch.
 */
export function useRevertAgencySpread() {
  const qc = useQueryClient();
  return useMutation<AgencySpreadRow, Error, { rateType: AgencyRateType }>({
    mutationFn: async ({ rateType }) => {
      const res = await api.post<ApiSpreadsRowResponse>(
        `/api/market-data/rate-spreads/${rateType}/revert`,
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.rateSpreads.list() });
      qc.invalidateQueries({ queryKey: queryKeys.marketRates.panel() });
    },
  });
}
